export type StakeRecordInput = {
  start: bigint; // unix timestamp in seconds
  stake_duration: bigint; // duration in seconds
  total_reward_wei: bigint; // contract calculateReward return value
  total_paid_out_wei: bigint; // contract total_paid_out field
};

export type RewardDrip = {
  is_matured: boolean;
  months_past_maturity: number; // 0..10 (clamped)
  drip_gross_wei: bigint; // (full/10) * months_past_maturity
  drip_net_wei: bigint; // max(0, gross - paid_out)
};

const THIRTY_DAYS_SEC = 30n * 86_400n;
const DRIP_MONTHS = 10n; // reward fully vests over 10 monthly 10% chunks

/**
 * Computes the reward drip state for a stake.
 * Pure function — no network calls, no side effects.
 */
export function computeRewardDrip(
  input: StakeRecordInput,
  nowSec: bigint,
): RewardDrip {
  const endSec = input.start + input.stake_duration;
  const is_matured = nowSec > endSec;

  if (!is_matured) {
    return {
      is_matured: false,
      months_past_maturity: 0,
      drip_gross_wei: 0n,
      drip_net_wei: 0n,
    };
  }

  // months_past_maturity is 1-based: 0 days past = month 1
  let months = Number((nowSec - endSec) / THIRTY_DAYS_SEC) + 1;
  if (months > Number(DRIP_MONTHS)) months = Number(DRIP_MONTHS);

  const gross = (input.total_reward_wei / DRIP_MONTHS) * BigInt(months);
  const net =
    gross > input.total_paid_out_wei ? gross - input.total_paid_out_wei : 0n;

  return {
    is_matured: true,
    months_past_maturity: months,
    drip_gross_wei: gross,
    drip_net_wei: net,
  };
}
